import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
    getAllTasksByTypeService,
    debitPackageCostService,
    debitProductCostService,
} from '../../../services/company.service';
import LoaderSpinner from '../../common/Loader';
import DatePickerComponent from '../../common/DatePickerComponent';
import InputCalculator from '../../common/InputCalculator';

/* istanbul ignore next */
const PostChargesModal = ({ show, handleCancel, productContext, onPosted }) => {
    const initialState = {
        taskId: 0,
        amount: '',
        date: new Date(),
        comment: '',
    };

    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [loader, setLoader] = useState(false);
    const [taskOptions, setTaskOptions] = useState([]);

    useEffect(() => {
        if (!show) return;
        setFormData(initialState);
        setErrors({});
        loadTasksForReference();
    }, [show, productContext?.referenceId, productContext?.referenceType]);

    const loadTasksForReference = async () => {
        const referenceId = productContext?.referenceId;
        const referenceType = productContext?.referenceType;
        if (!referenceId || !referenceType) {
            setTaskOptions([]);
            return;
        }
        try {
            const res = await getAllTasksByTypeService({
                taskReferenceId: referenceId,
                taskReferenceType: referenceType,
            });
            const tasks = res?.data?.EntityList || [];
            setTaskOptions(
                tasks.map((t) => {
                    const taskId = t.taskId ?? t.id;
                    return {
                        value: taskId,
                        label: `#${taskId} \u2014 ${t.taskTitle || 'Untitled'}`,
                    };
                })
            );
        } catch (e) {
            console.error(e);
            // Non-blocking: still allow standalone selection
            setTaskOptions([]);
        }
    };

    const linkOptions = useMemo(
        () => [{ value: 0, label: '\u2014 No task (standalone charge) \u2014' }, ...taskOptions],
        [taskOptions]
    );

    const validate = () => {
        const e = {};
        const amt = Number(formData.amount);
        if (!formData.amount || Number.isNaN(amt) || amt <= 0) e.amount = 'Enter a valid amount';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoader(true);
        try {
            const taskId = Number(formData.taskId);
            const isStandAlone = taskId <= 0;
            const referenceType = productContext?.referenceType;
            const basePayload = {
                taskId: isStandAlone ? null : taskId,
                debitAmount: Number(formData.amount),
                debitComment: formData.comment || '',
                transactionDate: formData.date.toISOString(),
                isStandAlone,
            };

            let response;
            if (referenceType === 'Package') {
                response = await debitPackageCostService({
                    ...basePayload,
                    packageId: productContext?.referenceId || 0,
                    packageTrackingNumber: productContext?.packageTrackingNumber || productContext?.sku || '',
                });
            } else {
                response = await debitProductCostService({
                    ...basePayload,
                    productId: productContext?.referenceId || productContext?.inboundProductId || 0,
                    returnProductSku: productContext?.returnProductSku || productContext?.sku || '',
                });
            }
            if (response?.data?.Status === 200) {
                toast.success('Charge posted successfully');
                if (onPosted) onPosted(response.data.Entity);
                handleCancel();
            } else {
                toast.error(
                    response?.data?.Message?.AppStatusDescription || 'Failed to post charge'
                );
            }
        } catch (err) {
            console.error(err);
            toast.error('An error occurred while posting the charge.');
        } finally {
            setLoader(false);
        }
    };

    const renderHeader = () => {
        const parts = [];
        if (productContext?.sku) parts.push(`SKU: ${productContext.sku}`);
        if (productContext?.productName) parts.push(productContext.productName);
        if (productContext?.sellerName) parts.push(productContext.sellerName);
        return parts.join('  \u00B7  ');
    };

    return (
        <Modal show={show} onHide={handleCancel} centered backdrop="static" size="lg">
            <Modal.Header>
                <div>
                    <Modal.Title>Post Charges</Modal.Title>
                    {renderHeader() && (
                        <div className="text-muted small mt-1">{renderHeader()}</div>
                    )}
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    className="btn btn-link p-0 border-0"
                    style={{ position: 'absolute', top: 12, right: 16, fontSize: 20, color: '#6c757d' }}
                    onClick={handleCancel}
                >
                    <i className="fa fa-times" />
                </button>
            </Modal.Header>
            <Modal.Body>
                {loader && (
                    <div className="full-screen-spinner-overlay">
                        <LoaderSpinner />
                    </div>
                )}
                <Row>
                    <Col xs={12} className="mb-3">
                        <div className="input-label-txt">
                            Link to Task{' '}
                            <span className="text-muted">
                                (optional &mdash; leave blank for standalone charge)
                            </span>
                        </div>
                        <Select
                            options={linkOptions}
                            value={
                                linkOptions.find(
                                    (o) => o.value === Number(formData.taskId)
                                ) || linkOptions[0]
                            }
                            onChange={(opt) =>
                                setFormData({ ...formData, taskId: opt?.value || 0 })
                            }
                            placeholder="Select Task"
                            isClearable={false}
                            menuPlacement="auto"
                            maxMenuHeight={180}
                        />
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">
                            Enter Amount ($) <span className="require-field">*</span>
                        </div>
                        <InputCalculator
                            value={String(formData.amount ?? '')}
                            onChange={(e) =>
                                setFormData({ ...formData, amount: e.target.value })
                            }
                            placeholder="Enter Amount"
                            maxLength={500}
                        />
                        {errors.amount && (
                            <span className="error-message">{errors.amount}</span>
                        )}
                    </Col>
                    <Col xs={12} md={6} className="mb-3">
                        <div className="input-label-txt">Date</div>
                        <DatePickerComponent
                            dateField={formData.date}
                            handleDateChange={(d) => setFormData({ ...formData, date: d })}
                            fieldName="date"
                            dateFormat="MM-dd-yyyy"
                        />
                    </Col>
                    <Col xs={12} className="mb-3">
                        <div className="input-label-txt">Comment</div>
                        <textarea
                            className="form-control"
                            rows={3}
                            placeholder="Enter comment..."
                            value={formData.comment}
                            onChange={(e) =>
                                setFormData({ ...formData, comment: e.target.value })
                            }
                        />
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <button
                    type="button"
                    className="btn cus-primary-bg-btn"
                    disabled={loader}
                    onClick={handleSubmit}
                >
                    Charge
                </button>
                <button
                    type="button"
                    className="btn cus-primary-transparent-btn"
                    disabled={loader}
                    onClick={handleCancel}
                >
                    Cancel
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default PostChargesModal;

PostChargesModal.propTypes = {
    show: PropTypes.bool,
    handleCancel: PropTypes.func,
    productContext: PropTypes.object,
    onPosted: PropTypes.func,
};
